import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Lock, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';
import authService from '../../services/authService';
import { useTheme } from '../../contexts/ThemeContext';
import LandingNav from '../Layout/LandingNav';
import LandingFooter from '../Layout/LandingFooter';
import SEO from '../SEO';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch('password');

  const onSubmit = async (data) => {
    if (!token) {
      toast.error('Invalid reset link. Please request a new one.');
      return;
    }

    try {
      setIsLoading(true);
      await authService.resetPassword(token, data.password);
      toast.success('Password reset successful! Please sign in.');
      navigate('/login');
    } catch (err) {
      if (err.message.includes('expired') || err.message.includes('invalid')) {
        toast.error('This reset link has expired. Please request a new one.');
      } else {
        toast.error(err.message || 'Failed to reset password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const requirements = [
    { label: 'At least 8 characters', met: password?.length >= 8 },
    { label: 'One uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'One lowercase letter', met: /[a-z]/.test(password) },
    { label: 'One number', met: /\d/.test(password) },
    { label: 'One special character', met: /[@$!%*?&.#^()\-_=+\[\]{}|;:,<>\/\\]/.test(password) },
  ];

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${d ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      <SEO
        title="Set new password"
        description="Set a new password for your PaperBrainz account."
        path="/reset-password"
        noindex
      />
      <LandingNav />

      <div className="flex-1 flex items-center justify-center px-4 pt-44 pb-32">
        <div className="relative z-10 w-full max-w-md space-y-8">
          {!token ? (
            /* Invalid token state */
            <>
              <div className="">
                <div className="text-center">
                  <div className="flex justify-center mb-6">
                    <div className={`w-14 h-14 rounded-[14px] flex items-center justify-center ${d ? 'bg-red-500/[0.08]' : 'bg-red-50'}`}>
                      <AlertCircle className={`w-6 h-6 ${d ? 'text-red-400' : 'text-red-500'}`} />
                    </div>
                  </div>
                  <h2 className={`text-3xl md:text-4xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'}`}>
                    Invalid Reset Link
                  </h2>
                  <p className={`mt-2 text-sm ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                    This password reset link is invalid or has expired.
                  </p>
                </div>

                <div className="mt-6 flex flex-col gap-3">
                  <Link
                    to="/forgot-password"
                    className={`
                      w-full flex items-center justify-center py-3 px-4 rounded-[12px] text-sm font-medium transition-all duration-200
                      ${d ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'}
                    `}
                  >
                    Request New Reset Link
                  </Link>
                  <Link
                    to="/login"
                    className={`
                      w-full flex items-center justify-center py-3 px-4 rounded-[12px] text-sm font-medium border transition-all duration-200
                      ${d ? 'border-white/[0.08] text-gray-300 hover:bg-white/[0.04]' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}
                    `}
                  >
                    Back to Sign In
                  </Link>
                </div>
              </div>
            </>
          ) : (
            /* Reset form */
            <>
              <div className="">
                <div className="text-center mb-10">
                  <h2 className={`text-3xl md:text-4xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'}`}>
                    Reset Your Password
                  </h2>
                  <p className={`mt-2 text-sm ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                    Enter your new password below
                  </p>
                </div>

                <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
                  {/* Password */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                      New Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <Lock className={`h-4.5 w-4.5 ${d ? 'text-gray-600' : 'text-gray-400'}`} />
                      </div>
                      <input
                        {...register('password', {
                          required: 'Password is required',
                          minLength: { value: 8, message: 'Password must be at least 8 characters' },
                          pattern: {
                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.#^()\-_=+\[\]{}|;:,<>\/\\])/,
                            message: 'Password must contain uppercase, lowercase, number, and special character',
                          },
                        })}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="Enter new password"
                        className={`
                          block w-full pl-10 pr-12 py-3 rounded-[12px] border text-sm transition-colors
                          ${errors.password
                            ? d ? 'border-red-500/40 focus:border-red-500' : 'border-red-300 focus:border-red-500'
                            : d ? 'border-white/[0.08] focus:border-blue-500/50' : 'border-gray-200 focus:border-blue-400'
                          }
                          ${d ? 'bg-white/[0.03] text-white placeholder-gray-600' : 'bg-gray-100 text-gray-900 placeholder-gray-400'}
                          focus:outline-none focus:ring-1 ${errors.password ? 'focus:ring-red-500/30' : 'focus:ring-blue-500/30'}
                        `}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center"
                      >
                        {showPassword ? (
                          <EyeOff className={`h-4.5 w-4.5 ${d ? 'text-gray-600 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'}`} />
                        ) : (
                          <Eye className={`h-4.5 w-4.5 ${d ? 'text-gray-600 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'}`} />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className={`mt-1.5 text-xs ${d ? 'text-red-400' : 'text-red-500'}`}>{errors.password.message}</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <Lock className={`h-4.5 w-4.5 ${d ? 'text-gray-600' : 'text-gray-400'}`} />
                      </div>
                      <input
                        {...register('confirmPassword', {
                          required: 'Please confirm your password',
                          validate: (value) => value === password || 'Passwords do not match',
                        })}
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="Confirm new password"
                        className={`
                          block w-full pl-10 pr-12 py-3 rounded-[12px] border text-sm transition-colors
                          ${errors.confirmPassword
                            ? d ? 'border-red-500/40 focus:border-red-500' : 'border-red-300 focus:border-red-500'
                            : d ? 'border-white/[0.08] focus:border-blue-500/50' : 'border-gray-200 focus:border-blue-400'
                          }
                          ${d ? 'bg-white/[0.03] text-white placeholder-gray-600' : 'bg-gray-100 text-gray-900 placeholder-gray-400'}
                          focus:outline-none focus:ring-1 ${errors.confirmPassword ? 'focus:ring-red-500/30' : 'focus:ring-blue-500/30'}
                        `}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className={`h-4.5 w-4.5 ${d ? 'text-gray-600 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'}`} />
                        ) : (
                          <Eye className={`h-4.5 w-4.5 ${d ? 'text-gray-600 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'}`} />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className={`mt-1.5 text-xs ${d ? 'text-red-400' : 'text-red-500'}`}>{errors.confirmPassword.message}</p>
                    )}
                  </div>

                  {/* Password Requirements */}
                  <div className={`rounded-[12px] border p-4 ${d ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-gray-50 border-gray-100'}`}>
                    <p className={`text-sm font-medium mb-2.5 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                      Password Requirements
                    </p>
                    <ul className="space-y-1.5">
                      {requirements.map((req) => (
                        <li key={req.label} className="flex items-center gap-2">
                          <Check className={`w-3.5 h-3.5 flex-shrink-0 transition-colors ${req.met ? 'text-emerald-400' : d ? 'text-gray-700' : 'text-gray-300'}`} />
                          <span className={`text-sm ${req.met ? (d ? 'text-emerald-400' : 'text-emerald-600') : (d ? 'text-gray-500' : 'text-gray-500')}`}>
                            {req.label}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`
                      w-full flex items-center justify-center py-3 px-4 rounded-[12px] text-sm font-medium transition-all duration-200
                      ${isLoading
                        ? d ? 'bg-white/10 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : d ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'
                      }
                    `}
                  >
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </form>
              </div>

              {/* Back to login card */}
              <Link
                to="/login"
                className={`block rounded-[16px] border p-4 text-center text-sm transition-colors duration-300 ${
                  d ? 'bg-[#111] border-white/[0.06] text-gray-400 hover:text-gray-300' : 'bg-white border-gray-200 shadow-sm text-gray-500 hover:text-gray-700'
                }`}
              >
                Back to Sign In
              </Link>
            </>
          )}
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}

export default ResetPasswordPage;
